/*!
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { CircularProgress, Snackbar } from '@mui/material';
import { Alert } from '@mui/lab';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import React, { useContext } from 'react';
import { Redirect, Route, useRouteMatch } from 'react-router-dom';
import { AdminAuthContext } from '../Contexts/AdminAuthContext';

export const SecureRoute = ({ errorComponent, ...routeProps }) => {
    const { oktaAuth, authState } = useOktaAuth();
    const match = useRouteMatch();
    const isPending = React.useRef(false);
    const [error, setError] = React.useState(null);
    const {
        isExpenseUser,
        isTimesheetUser,
        isInvoicingUser,
        isExpenseSystemAdministratorUser,
        adminUser,
        isAuthenticated,
    } = useContext(AdminAuthContext);

    React.useEffect(() => {
        const login = async () => {
            if (isPending.current) {
                return;
            }

            isPending.current = true;

            const originalUri = toRelativeUrl(window.location.href, window.location.origin);
            oktaAuth.setOriginalUri(originalUri);
            await oktaAuth.signInWithRedirect();
        };

        // Only process logic if the route matches
        if (!match) {
            return;
        }

        if (!authState) {
            return;
        }

        if (authState.isAuthenticated) {
            isPending.current = false;
            return;
        }

        if (!authState.isAuthenticated) {
            login().catch((err) => {
                setError(err);
            });
        }
    }, [authState, oktaAuth, match, isPending, setError]);

    if (error && errorComponent) {
        const ErrorComponent = errorComponent;
        return <ErrorComponent error={error} />;
    }

    if (!authState || !isAuthenticated) {
        return <CircularProgress style={{ margin: 'auto' }} color="primary" />;
    }

    if (isAuthenticated && !adminUser?.groups) {
        return (
            <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error">
                    Please reach out to your manager to request access to the Time & Expense App
                </Alert>
            </Snackbar>
        );
    }

    if (isAuthenticated && adminUser?.groups) {
        const isTimesheetRoute = routeProps.path.toLowerCase().includes('timesheet');
        const isExpenseRoute = routeProps.path.toLowerCase().includes('expense');
        const isCreditCardRoute = routeProps.path.toLowerCase().includes('credit-card');
        const isInvoicingRoute = routeProps.path.toLowerCase().includes('invoicing');
        const hasRouteAccess =
            (isTimesheetRoute && isTimesheetUser) ||
            (isExpenseRoute && isExpenseUser) ||
            (isCreditCardRoute && isExpenseSystemAdministratorUser) ||
            isInvoicingRoute;
        const hasBaseAccess =
            !isTimesheetRoute &&
            !isExpenseRoute &&
            !isCreditCardRoute &&
            !isInvoicingRoute &&
            (isExpenseUser || isTimesheetUser || isInvoicingUser);

        // Redirect to accessible base path
        if (hasBaseAccess) {
            const basePath = isTimesheetUser ? (
                <Redirect to="/timesheets" />
            ) : isExpenseUser ? (
                <Redirect to="/expenses" />
            ) : (
                <Route {...routeProps} />
            );
            return basePath;
        }

        if (!hasRouteAccess) {
            const accessRoute = isExpenseRoute
                ? 'Expense Management'
                : isTimesheetRoute
                ? 'Timesheet Management'
                : isCreditCardRoute
                ? 'Credit Card Management'
                : 'this application';
            return (
                <Snackbar open anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert severity="error">
                        Please reach out to your manager to request access to {accessRoute}
                    </Alert>
                </Snackbar>
            );
        }
    }

    return <Route {...routeProps} />;
};

export default SecureRoute;
